import React, { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

/**
 * InstallAppButton — Progressive Web App install prompt that ALWAYS shows
 * between the hero and the chat (except when the app is already running
 * standalone, i.e. has been installed and launched from the Home Screen).
 *
 * Click behavior, by platform:
 *   - Chrome / Edge / Android (beforeinstallprompt fired) — opens the
 *     browser's native install dialog directly.
 *   - Chrome / Edge desktop (event not yet fired) — opens our instructions
 *     modal showing the address-bar install icon path.
 *   - iOS Safari — opens our instructions modal with Add-to-Home-Screen
 *     steps (iOS doesn't expose programmatic install).
 *   - Other browsers — instructions modal with platform-detected guidance.
 *
 * Result: the user ALWAYS has a clear path to install, regardless of
 * which browser they showed up in.
 */
export default function InstallAppButton() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showHelp, setShowHelp] = useState(false);
    const [platform, setPlatform] = useState("generic");
    const [isStandalone, setIsStandalone] = useState(false);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        setIsStandalone(standalone);

        const ua = window.navigator.userAgent.toLowerCase();
        const isIOS =
            /iphone|ipad|ipod/.test(ua) &&
            !ua.includes("crios") &&
            !ua.includes("fxios");
        const isAndroid = /android/.test(ua);
        const isChromeDesktop =
            /chrome|edg/.test(ua) && !isIOS && !isAndroid && !/mobile/.test(ua);
        const isFirefox = /firefox|fxios/.test(ua);
        const isSafariDesktop = /safari/.test(ua) && !/chrome|edg/.test(ua) && !isIOS;

        if (isIOS) setPlatform("ios");
        else if (isAndroid) setPlatform("android");
        else if (isChromeDesktop) setPlatform("chrome-desktop");
        else if (isFirefox) setPlatform("firefox");
        else if (isSafariDesktop) setPlatform("safari-desktop");
        else setPlatform("generic");

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            if (choice.outcome === "accepted") {
                setInstalled(true);
            }
            setDeferredPrompt(null);
            return;
        }
        setShowHelp(true);
    };

    // Only hidden when the app is actually installed and running standalone.
    if (isStandalone || installed) return null;

    return (
        <>
            <section
                className="flex justify-center px-4"
                data-testid="install-app-section"
            >
                <button
                    type="button"
                    onClick={handleInstall}
                    data-testid="install-app-button"
                    className="group inline-flex items-center gap-3 px-6 sm:px-7 py-3 sm:py-3.5 rounded-full border border-[#D4AF37]/40 bg-[#0A1220]/65 backdrop-blur-sm hover:bg-[#0A1220]/85 hover:border-[#D4AF37]/70 transition-all duration-200 shadow-[0_2px_14px_rgba(0,0,0,0.45)]"
                >
                    <Download
                        size={18}
                        className="text-[#D4AF37] flex-shrink-0 transition-transform group-hover:-translate-y-0.5"
                    />
                    <span className="flex flex-col items-start leading-tight">
                        <span className="font-mono-os text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#D4AF37]">
                            Install the App
                        </span>
                        <span className="font-serif-display text-sm sm:text-base text-[#F9E596]">
                            Carry the Codex with you
                        </span>
                    </span>
                </button>
            </section>

            {showHelp && (
                <InstallHelpModal
                    platform={platform}
                    onClose={() => setShowHelp(false)}
                />
            )}
        </>
    );
}

function InstallHelpModal({ platform, onClose }) {
    const steps = INSTRUCTIONS[platform] || INSTRUCTIONS.generic;
    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="install-help-modal"
        >
            <div
                className="codex-card w-full max-w-md p-6 sm:p-7 relative max-h-[88vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-[#94A3B8] hover:text-[#F9E596] transition-colors"
                    aria-label="Close"
                    data-testid="install-help-close"
                >
                    <X size={20} />
                </button>
                <h3 className="font-serif-display text-xl sm:text-2xl text-[#F9E596] mb-1 leading-tight">
                    {steps.title}
                </h3>
                <p className="font-mono-os text-[10px] sm:text-xs tracking-[0.24em] uppercase text-[#D4AF37] mb-4">
                    {steps.subtitle}
                </p>
                <ol className="space-y-3 text-[#E2E8F0] text-sm sm:text-base leading-relaxed">
                    {steps.steps.map((step, i) => (
                        <li className="flex gap-3" key={i}>
                            <span className="font-mono-os text-xs tracking-[0.24em] text-[#D4AF37] mt-0.5 flex-shrink-0">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span>{step}</span>
                        </li>
                    ))}
                </ol>
                <p className="text-xs text-[#94A3B8] italic mt-5 pt-4 border-t border-[#D4AF37]/15">
                    {steps.footer}
                </p>
            </div>
        </div>
    );
}

const INSTRUCTIONS = {
    ios: {
        title: "Install on iPhone or iPad",
        subtitle: "Safari · Add to Home Screen",
        steps: [
            <>
                Tap the{" "}
                <Share size={16} className="inline align-middle text-[#D4AF37] mx-1" />{" "}
                Share button at the bottom of Safari.
            </>,
            <>
                Scroll and tap{" "}
                <span className="text-[#F9E596] font-medium">
                    &ldquo;Add to Home Screen&rdquo;
                </span>
                .
            </>,
            <>
                Tap <span className="text-[#F9E596] font-medium">Add</span> in
                the top-right corner. The Codex now sits on your Home Screen
                like a native app.
            </>,
        ],
        footer:
            "iOS doesn\u2019t expose a one-tap install, so the Share menu is the only path. Once installed, the Codex opens full-screen with no browser chrome.",
    },
    android: {
        title: "Install on Android",
        subtitle: "Chrome · Add to Home Screen",
        steps: [
            <>Tap the three-dot menu in Chrome&rsquo;s top-right corner.</>,
            <>
                Tap{" "}
                <span className="text-[#F9E596] font-medium">
                    &ldquo;Install app&rdquo;
                </span>{" "}
                (or{" "}
                <span className="text-[#F9E596] font-medium">
                    &ldquo;Add to Home Screen&rdquo;
                </span>{" "}
                on older Chrome).
            </>,
            <>
                Confirm by tapping{" "}
                <span className="text-[#F9E596] font-medium">Install</span>. The
                Codex opens like a native app from your launcher.
            </>,
        ],
        footer:
            "After a few seconds of browsing, Chrome may also offer a one-tap install banner at the bottom of the screen.",
    },
    "chrome-desktop": {
        title: "Install on Desktop",
        subtitle: "Chrome / Edge · Install icon",
        steps: [
            <>
                Look for the install icon at the right end of the address bar
                (a small monitor with a down-arrow, or a + symbol).
            </>,
            <>
                Click it and choose{" "}
                <span className="text-[#F9E596] font-medium">Install</span>.
            </>,
            <>
                The Codex opens in its own window like a native desktop app.
                You can pin it to the taskbar or dock.
            </>,
        ],
        footer:
            "If you don\u2019t see the install icon yet, give the page 30 seconds of use, then check again. Chrome only surfaces install once the app meets engagement criteria.",
    },
    "safari-desktop": {
        title: "Save to Dock on Mac",
        subtitle: "Safari · Add to Dock",
        steps: [
            <>
                In Safari&rsquo;s top menu, choose{" "}
                <span className="text-[#F9E596] font-medium">File</span> &rarr;{" "}
                <span className="text-[#F9E596] font-medium">Add to Dock</span>.
            </>,
            <>
                Confirm the name and click{" "}
                <span className="text-[#F9E596] font-medium">Add</span>.
            </>,
            <>
                The Codex now lives in your Dock and opens as its own app
                window.
            </>,
        ],
        footer:
            "Add to Dock requires macOS Sonoma or later. On earlier macOS, bookmark the page and pin the bookmark to the Favorites bar.",
    },
    firefox: {
        title: "Install on Firefox",
        subtitle: "Firefox · Bookmark + Pin",
        steps: [
            <>
                Firefox doesn&rsquo;t support installing PWAs directly.
                Bookmark this page with{" "}
                <span className="text-[#F9E596] font-medium">Ctrl/Cmd + D</span>
                .
            </>,
            <>
                For an app-like experience, use{" "}
                <span className="text-[#F9E596] font-medium">Chrome</span> or{" "}
                <span className="text-[#F9E596] font-medium">Edge</span> to
                install, or open on iPhone / Android for one-tap install.
            </>,
        ],
        footer:
            "Mozilla removed the install prompt on Firefox desktop. The mobile site still works perfectly in Firefox \u2014 just open and use.",
    },
    generic: {
        title: "Install the Codex",
        subtitle: "Browser-specific path",
        steps: [
            <>
                On{" "}
                <span className="text-[#F9E596] font-medium">iPhone / iPad</span>
                : open in Safari, tap Share, then Add to Home Screen.
            </>,
            <>
                On <span className="text-[#F9E596] font-medium">Android</span>:
                open in Chrome, tap the three-dot menu, then Install app.
            </>,
            <>
                On <span className="text-[#F9E596] font-medium">Desktop</span>:
                open in Chrome or Edge, then click the install icon at the
                right end of the address bar.
            </>,
        ],
        footer:
            "Once installed, the Codex opens full-screen like a native app, with no browser chrome.",
    },
};
